// ═══════════════════════════════════════════════════════════════
// CAPACITY WEIGHTS SERVICE
// Path: src/services/capacityWeightsService.js
//
// Chapter-level override of the 6 confidence component weights.
// Stored as JSON in app_settings.capacity_weights — same pattern
// as email_templates. Overrides are merged on top of defaults so
// a chapter only has to store values they've changed.
//
// Weights are normalized (sum = 1) before use; the UI can store
// raw percentages and let the scoring engine rebalance silently
// for components with no possible data (e.g. external_wealth
// when there are no wealth imports).
// ═══════════════════════════════════════════════════════════════

import { settingsService } from "./settingsService";
import { debugLog } from "../utils/debugLog";
import { DEFAULT_CAPACITY_WEIGHTS } from "../utils/capacityScoring";

const SETTINGS_KEY = "capacity_weights";

const normalize = (weights) => {
  const sum = Object.values(weights).reduce((a, b) => a + b, 0);
  if (sum === 0) return { ...DEFAULT_CAPACITY_WEIGHTS };
  const normalized = {};
  Object.keys(weights).forEach((k) => {
    normalized[k] = weights[k] / sum;
  });
  return normalized;
};

const mergeWithDefaults = (stored) => {
  if (!stored) return { ...DEFAULT_CAPACITY_WEIGHTS };
  const merged = { ...DEFAULT_CAPACITY_WEIGHTS, ...stored };
  return normalize(merged);
};

export const capacityWeightsService = {
  async get() {
    try {
      const stored = await settingsService.get(SETTINGS_KEY);
      return mergeWithDefaults(stored);
    } catch (err) {
      debugLog.error("capacityWeightsService.get", err);
      return { ...DEFAULT_CAPACITY_WEIGHTS };
    }
  },

  async update(weights) {
    try {
      const normalized = normalize(weights);
      await settingsService.set(SETTINGS_KEY, normalized);
      debugLog.action("capacityWeightsService.update", normalized);
      return normalized;
    } catch (err) {
      debugLog.error("capacityWeightsService.update", err);
      throw err;
    }
  },

  async reset() {
    try {
      await settingsService.set(SETTINGS_KEY, { ...DEFAULT_CAPACITY_WEIGHTS });
      return { ...DEFAULT_CAPACITY_WEIGHTS };
    } catch (err) {
      debugLog.error("capacityWeightsService.reset", err);
      throw err;
    }
  },
};
